import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  Polygon,
} from "@react-google-maps/api";
import {
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  createPolygon,
  fetchAreasByCityId,
  fetchCities,
  getNetworkPartnerByAreaCode,
  getPolygonDetailNormal,
  getPolygonsByCityId,
} from "redux/actions/Master";
import { AxiosResponse } from "axios";
import { CityModel } from "types/models/CityModel";
import { AreaModel } from "types/models/AreaModel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteMessage from "../PolygonTable/DeleteMessage";
interface MapLocation {
  lat: number;
  lng: number;
}

const containerStyle = {
  width: '100%',
  height: '100vh',
};
const MapComponent: React.FC = () => {
  const [deleteMarkerToRemove, setDeleteMarkerToRemove] = useState<google.maps.Marker | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [polygonToDelete, setPolygonToDelete] = useState<google.maps.Polygon | null>(null);

  const { isLoaded } = useOutletContext<{ isLoaded: boolean }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [polygonPaths, setPolygonPaths] = useState<Array<Array<{ lat: number; lng: number }>>>([]);
  const [polygonRefs, setPolygonRefs] = useState<google.maps.Polygon[]>([]);
  const [cities, setCities] = useState<CityModel[]>([]);
  const [areas, setAreas] = useState<AreaModel[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedAreaObject, setSelectedAreaObject] = useState<AreaModel | undefined>();
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [pinCode, setPinCode] = useState<string>('');
  const [latitude, setLatitude] = useState<string | null>(null);
  const [longitude, setLongitude] = useState<string | null>(null);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? 'YOUR_BACKUP_API_KEY';
  const defaultCenter = { lat: 28.6139, lng: 77.209 };
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [polygonDeleteMarkers, setPolygonDeleteMarkers] = useState<
    google.maps.Marker[]
  >([]);
  const [networkPartners, setNetworkPartners] = useState<any[]>([]);
  const [networkPartner, setNetworkPartner] = useState<string>('');
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingVertices, setDrawingVertices] = useState<MapLocation[]>([]);
  const isDrawingModeRef = useRef(false);
  const [editablePolygonPaths, setEditablePolygonPaths] = useState<MapLocation[][]>([]);
  const [newlyCreatedPolygonPaths, setNewlyCreatedPolygonPaths] = useState<MapLocation[][]>([]);
  const [originalEditablePolygonPaths, setOriginalEditablePolygonPaths] = useState<MapLocation[][]>([]);

  useEffect(() => {
    const fetchCitiesData = async () => {
      try {
        const response = (await dispatch(fetchCities())) as unknown as AxiosResponse;
        if (response?.status === 200) {
          setCities(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCitiesData();
  }, [dispatch]);

  useEffect(() => {

    return () => {
      polygonRefs.forEach(polygon => {
        google.maps.event.clearInstanceListeners(polygon);
      });
    };
  }, [polygonRefs]);

  useEffect(() => {
    isDrawingModeRef.current = isDrawingMode;
  }, [isDrawingMode]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ draggableCursor: isDrawingMode ? 'crosshair' : '' });
    polygonRefs.forEach(p => p.setOptions({ clickable: !isDrawingMode }));
    polygonDeleteMarkers.forEach(m => m.setClickable(!isDrawingMode));
  }, [isDrawingMode, polygonRefs, polygonDeleteMarkers]);

  const clearMapPolygons = () => {
    polygonRefs.forEach(polygon => polygon.setMap(null));
    polygonDeleteMarkers.forEach(marker => marker.setMap(null));
    setPolygonRefs([]);
    setPolygonPaths([]);
    setPolygonDeleteMarkers([]);
  };

  const handleCityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const cityName = event.target.value;

    setSelectedCity(cityName);
    setAreas([]);
    setSelectedArea('');
    setSelectedAreaObject(undefined);

    setNetworkPartner('');
    setNetworkPartners([]);

    clearMapPolygons();

    const selectedCityData = cities.find(city => city.name === cityName);
    if (selectedCityData) {
      try {
        const fetchedAreas = (await dispatch(fetchAreasByCityId(selectedCityData.id))) as unknown as AreaModel[];
        setAreas(fetchedAreas);

        const cityPolygons = await dispatch(getPolygonsByCityId(selectedCityData.id)) as any[];

        cityPolygons.forEach((polygonData) => {
          if (polygonData.latlongarray && polygonData.latlongarray.trim() !== '') {
            const paths = JSON.parse(polygonData.latlongarray);
            const polygonGroups = Array.isArray(paths[0]) ? paths : [paths];

            polygonGroups.forEach((path: Array<{ lat: number; lng: number }>) => {
              const googleMapPath = path.map(coord => new window.google.maps.LatLng(coord.lat, coord.lng));

              const polygon = new window.google.maps.Polygon({
                paths: googleMapPath,
                map: mapRef.current!,
                editable: true,
                draggable: false,
                fillColor: '#ff0033',
                fillOpacity: 0.3,
                strokeColor: '#ff0033',
                strokeOpacity: 0.8,
                strokeWeight: 2,
              });

              setPolygonRefs(prev => [...prev, polygon]);
              setPolygonPaths(prev => [...prev, path]);
              addDeleteMarker(polygon);
            });
          }
        });

      } catch (error) {
        console.error('Error fetching areas or polygons by city:', error);
      }
    }
  };





  const handleAreaChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectedArea(value);
    setSelectedAreaObject(undefined);

    setNetworkPartner('');
    setNetworkPartners([]);

    const areaObj = areas.find(area => area.areaId === Number(value));
    setSelectedAreaObject(areaObj);

    if (areaObj?.areaCode) {
      try {
        const response = await dispatch(getNetworkPartnerByAreaCode(areaObj.areaCode)) as unknown as AxiosResponse;
        if (response?.status === 200) {
          setNetworkPartners(response.data.data);
        } else {
          setNetworkPartners([]);
        }
      } catch (error) {
        console.error("Error fetching network partners:", error);
        setNetworkPartners([]);
      }
    } else {
      setNetworkPartners([]);
    }
  };



  const updateDeleteMarkerPosition = (polygon: google.maps.Polygon, index: number) => {
    const bounds = new window.google.maps.LatLngBounds();
    polygon.getPath().forEach((coord) => bounds.extend(coord));
    const topRight = bounds.getNorthEast();
    const deleteMarker = polygonDeleteMarkers[index];
    if (deleteMarker) {
      deleteMarker.setPosition(
        new window.google.maps.LatLng(topRight.lat() - 0.0005, topRight.lng() + 0.0006)
      );
    }
  };

  const calculateCentroid = (path: MapLocation[]) => {
    const avg = path.reduce(
      (sum, p) => ({ lat: sum.lat + p.lat, lng: sum.lng + p.lng }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: avg.lat / path.length,
      lng: avg.lng / path.length,
    };
  };
  const handlePolygonComplete = (polygon: google.maps.Polygon) => {
    const path: MapLocation[] = polygon.getPath().getArray().map((point) => ({
      lat: point.lat(),
      lng: point.lng(),
    }));

    setPolygonPaths(prev => [...prev, path]);
    setPolygonRefs(prev => [...prev, polygon]);
    setNewlyCreatedPolygonPaths(prev => [...prev, path]);

    const index = newlyCreatedPolygonPaths.length;

    const { lat, lng } = calculateCentroid(path);
    setLatitude(lat.toString());
    setLongitude(lng.toString());

    polygon.setOptions({
      editable: true,
      draggable: false,
      fillColor: "#ff0033",
      fillOpacity: 0.3,
      strokeColor: "#ff0033",
      strokeOpacity: 0.8,
      strokeWeight: 2,
    });

    addDeleteMarker(polygon);

    const updatePathOnEdit = () => {
      const updatedPath = polygon.getPath().getArray().map((point) => ({
        lat: point.lat(),
        lng: point.lng(),
      }));

      setNewlyCreatedPolygonPaths(prev => {
        const newPaths = [...prev];
        newPaths[index] = updatedPath;
        return newPaths;
      });
    };

    polygon.getPath().addListener("set_at", updatePathOnEdit);
    polygon.getPath().addListener("insert_at", updatePathOnEdit);
    polygon.getPath().addListener("remove_at", updatePathOnEdit);
  };




  const finishDrawing = () => {
    if (drawingVertices.length < 3) return;
    const polygon = new window.google.maps.Polygon({
      paths: drawingVertices.map(v => new window.google.maps.LatLng(v.lat, v.lng)),
      map: mapRef.current!,
    });
    handlePolygonComplete(polygon);
    setDrawingVertices([]);
    setIsDrawingMode(false);
  };

  const handleGoClick = useCallback(async () => {
    if (!selectedCity || !mapRef.current) return;

    const map = mapRef.current;
    const areaParam = selectedAreaObject ? selectedAreaObject.area : '';
    const cityParam = selectedCity;
    const address = selectedAreaObject
      ? `${selectedAreaObject.area}, ${selectedCity}`
      : selectedCity;

    try {

      polygonRefs.forEach(p => p.setMap(null));
      setPolygonRefs([]);
      setPolygonPaths([]);
      polygonDeleteMarkers.forEach(marker => marker.setMap(null));
      setPolygonDeleteMarkers([]);
      const polygonDataList = await dispatch(
        getPolygonDetailNormal(areaParam, cityParam)
      ) as any[];

      console.log(polygonDataList);

      if (polygonDataList.length > 0) {
        setPinCode(polygonDataList[0].pincode);
        setLatitude(polygonDataList[0].latitude);
        setLongitude(polygonDataList[0].longitude);
      }

      if (Array.isArray(polygonDataList) && polygonDataList.length > 0) {
        setEditablePolygonPaths([]);
        polygonDataList.forEach((polygonData) => {
          const isEditablePolygon =
            selectedAreaObject &&
            polygonData.area_name?.toLowerCase() === selectedAreaObject.area?.toLowerCase();

          const fillColor = isEditablePolygon ? '#ff0033' : '#6d6dae';
          const editable = !!isEditablePolygon;

          if (polygonData?.latlongarray && polygonData.latlongarray.trim() !== '') {
            const paths = JSON.parse(polygonData.latlongarray);
            const polygonGroups = Array.isArray(paths[0]) ? paths : [paths];

            polygonGroups.forEach((path: Array<{ lat: number; lng: number }>) => {
              const googleMapPath = path.map(coord => new window.google.maps.LatLng(coord.lat, coord.lng));

              const polygon = new window.google.maps.Polygon({
                paths: googleMapPath,
                map,
                editable,
                draggable: false,
                fillColor,
                fillOpacity: 0.3,
                strokeColor: fillColor,
                strokeOpacity: 0.8,
                strokeWeight: 2,
              });

              setPolygonRefs(prev => [...prev, polygon]);
              setPolygonPaths(prev => [...prev, path]);

              if (isEditablePolygon && path.length > 0) {
                const index = editablePolygonPaths.length;
                setEditablePolygonPaths(prev => [...prev, path]);
                setOriginalEditablePolygonPaths(prev => [...prev, path]); 
                addDeleteMarker(polygon);

                const pathRef = polygon.getPath();

                const updateEditablePath = () => {
                  const updated = pathRef.getArray().map((point) => ({
                    lat: point.lat(),
                    lng: point.lng(),
                  }));

                  setEditablePolygonPaths(prev => {
                    const newPaths = [...prev];
                    newPaths[index] = updated;
                    return newPaths;
                  });

                  const { lat, lng } = calculateCentroid(updated);
                  setLatitude(lat.toString());
                  setLongitude(lng.toString());

                  fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
                  )
                    .then(res => res.json())
                    .then(data => {
                      const postalCode = data.results[0]?.address_components?.find(
                        (comp: any) => comp.types.includes("postal_code")
                      )?.long_name;
                      if (postalCode) setPinCode(postalCode);
                    })
                    .catch(err => console.error("Geocoding failed:", err));
                };

                pathRef.addListener("set_at", updateEditablePath);
                pathRef.addListener("insert_at", updateEditablePath);
                pathRef.addListener("remove_at", updateEditablePath);
              }
            });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching polygons:', error);
    }
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
      );
      const data = await response.json();

      if (data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setLocation({ lat, lng });
        setLatitude(lat.toString());
        setLongitude(lng.toString());

        map.panTo({ lat, lng });
        map.setZoom(15);
      } else {
        map.panTo(defaultCenter);
        map.setZoom(10);
      }
    } catch (error) {
      console.error('Error fetching location from geocode API:', error);
      map.panTo(defaultCenter);
      map.setZoom(10);
    }
  }, [
    selectedCity,
    selectedAreaObject,
    dispatch,
    googleMapsApiKey,
    polygonRefs,
    polygonDeleteMarkers,
  ]);

  const savePolygonGIS = async () => {

    if (!selectedArea || !selectedCity) {
      setErrorSnackbar(true);
      return;
    }
    const editedPolygons = editablePolygonPaths.map((path, index) => {
  const original = originalEditablePolygonPaths[index];
  return JSON.stringify(path) !== JSON.stringify(original) ? path : original;
});
    const updatedPolygonPathsNew = [
        ...editedPolygons,
      ...newlyCreatedPolygonPaths,
    ].filter(path => path.length > 0);

    const updatedPolygonPaths = updatedPolygonPathsNew ? updatedPolygonPathsNew : [];
    //const updatedPolygonPaths = polygonPaths.filter(path => path && path.length > 0);
    try {
      const groupid = `${Math.floor(Date.now() / 1000)}_${Math.floor(Math.random() * 1000)}`;
      const polygonData = {
        areaid: selectedArea,
        societyid: selectedCity,
        isActive: true,
        latlongarray: updatedPolygonPaths.length === 0 ? null : JSON.stringify(updatedPolygonPaths),
        groupid,
        latitude: latitude ?? '',
        longitude: longitude ?? '',
        path: '',
        pincode: pinCode,
        type: 'polygon',
      };

      const response = (await dispatch(createPolygon(polygonData))) as unknown as AxiosResponse;
      if (response?.status === 200) {
        setSuccessSnackbar(true);
        setTimeout(() => {
          navigate('/master/manageViewPolygon');
        }, 700);
      } else {
        setErrorSnackbar(true);
        throw new Error('Failed to save polygons');
      }
    } catch (error) {
      setErrorSnackbar(true);
    }
  };



  const handleConfirmDelete = () => {
    if (!polygonToDelete || !deleteMarkerToRemove) return;

    polygonToDelete.setMap(null);
    deleteMarkerToRemove.setMap(null);
    const deletedPath = polygonToDelete.getPath().getArray().map((point) => ({
      lat: point.lat(),
      lng: point.lng(),
    }));
    setPolygonRefs((prevPolygons) => {
      const index = prevPolygons.indexOf(polygonToDelete);
      if (index === -1) return prevPolygons;

      setPolygonPaths((prevPaths) => {
        const newPaths = [...prevPaths];
        newPaths.splice(index, 1);
        return newPaths;
      });

      return prevPolygons.filter((p) => p !== polygonToDelete);
    });
    setEditablePolygonPaths((prev) => {
      if (!prev) return prev;
      const filtered = prev.filter(path => JSON.stringify(path) !== JSON.stringify(deletedPath));
      return filtered;
    });
    setNewlyCreatedPolygonPaths((prev) => {
      if (!prev) return prev;
      const filtered = prev.filter(path => JSON.stringify(path) !== JSON.stringify(deletedPath));
      return filtered;
    });
    setPolygonDeleteMarkers((prevMarkers) =>
      prevMarkers.filter((m) => m !== deleteMarkerToRemove)
    );

    // Reset deletion state
    setOpenDeleteDialog(false);
    setPolygonToDelete(null);
    setDeleteMarkerToRemove(null);
  };

  const handleReset = () => {
    setSelectedCity('');
    setSelectedArea('');
    setSelectedAreaObject(undefined);
    setNetworkPartner('');
    setNetworkPartners([]);
    setLatitude(null);
    setLongitude(null);
    setLocation(null);
    setPinCode('');
    setEditablePolygonPaths([]);
    setNewlyCreatedPolygonPaths([]);
    setOriginalEditablePolygonPaths([]);

    // Only remove editable polygon and delete marker
    polygonRefs.forEach((polygon, index) => {
      if (polygon.getEditable()) {
        polygon.setMap(null);
        const marker = polygonDeleteMarkers[index];
        if (marker) marker.setMap(null);
      }
    });

    // Keep city-wide polygons (read-only)
    setPolygonRefs(prev => prev.filter(p => !p.getEditable()));
    setPolygonDeleteMarkers(prev => prev.filter((_, idx) => !polygonRefs[idx]?.getEditable()));
    setPolygonPaths(prev => prev.filter((_, idx) => !polygonRefs[idx]?.getEditable()));
  };


  const addDeleteMarker = (polygon: google.maps.Polygon) => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const path = polygon.getPath();

    const computeCentroid = () => {
      let latSum = 0;
      let lngSum = 0;
      const numPoints = path.getLength();
      for (let i = 0; i < numPoints; i++) {
        const latLng = path.getAt(i);
        latSum += latLng.lat();
        lngSum += latLng.lng();
      }
      return new window.google.maps.LatLng(latSum / numPoints, lngSum / numPoints);
    };

    const deleteMarker = new window.google.maps.Marker({
      position: computeCentroid(),
      map,
      icon: {
        url: "https://img.icons8.com/ios-filled/50/FF0000/trash.png",
        scaledSize: new window.google.maps.Size(15, 15),
      },
      title: "Delete Polygon",
      clickable: true,
    });

    deleteMarker.addListener("click", () => {
      setPolygonToDelete(polygon);
      setDeleteMarkerToRemove(deleteMarker);
      setOpenDeleteDialog(true);
    });

    const updateMarkerPosition = () => {
      deleteMarker.setPosition(computeCentroid());
    };

    path.addListener("insert_at", updateMarkerPosition);
    path.addListener("set_at", updateMarkerPosition);
    path.addListener("remove_at", updateMarkerPosition);

    setPolygonDeleteMarkers((prevMarkers) => [...prevMarkers, deleteMarker]);
  };

  useEffect(() => {
    console.log("Current pinCode:", pinCode);
  }, [pinCode]);


  return (
    <>
      <Box sx={{ position: 'relative', width: '100%', height: '100vh' }}>
        <Box
          position="absolute"
          top={60}
          left={12}
          sx={{
            zIndex: 1,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            p: 2,
            borderRadius: 1,
            boxShadow: 3,
            width: { xs: '90%', sm: '400px' },
            maxWidth: '600px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/master/manageViewPolygon')}
              sx={{ textTransform: 'none', color: 'black', padding: 0, minWidth: 'auto' }}
            />
            <Typography variant="h4" sx={{ mb: 0, ml: 1 }}>
              Find Your Locality
            </Typography>
          </Box>

          <FormControl fullWidth sx={{ height: '50px', mb: 2 }}>
            <TextField
              label="Select City"
              value={selectedCity}
              onChange={handleCityChange}
              select
              fullWidth
              sx={{ '& .MuiInputBase-input': { height: '36px', padding: '8px' } }}
              SelectProps={{
                MenuProps: {
                  PaperProps: { sx: { maxHeight: '200px' } },
                },
              }}
            >
              {cities.map(city => (
                <MenuItem key={city.id} value={city.name}>
                  {city.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>

          {selectedCity && (
            <Box display="flex" flexDirection="column" gap={2}>
              <FormControl fullWidth sx={{ height: '50px' }}>
                <TextField
                  label="Select Area"
                  value={selectedArea}
                  onChange={handleAreaChange}
                  select
                  fullWidth
                  sx={{ '& .MuiInputBase-input': { height: '36px', padding: '8px' } }}
                  SelectProps={{
                    MenuProps: {
                      PaperProps: {
                        sx: { maxHeight: '200px' }
                      }
                    },
                  }}
                >
                  {areas.map(area => (
                    <MenuItem key={area.areaId} value={area.areaId}>
                      {area.area}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <Box display="flex" alignItems="center" gap={2}>
                <FormControl sx={{ flex: 1 }}>
                  <TextField
                    label="Network Partner"
                    value={networkPartner}
                    onChange={(e) => setNetworkPartner(e.target.value)}
                    select
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': { height: '40px' },
                      '& .MuiInputBase-input': { padding: '8px' }
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: { maxHeight: '200px' }
                        }
                      },
                    }}
                  >
                    {networkPartners.length > 0 ? (
                      networkPartners.map((np) => (
                        <MenuItem key={np.id} value={np.partner_name}>
                          {np.partner_name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No Network Partners Available</MenuItem>
                    )}
                  </TextField>
                </FormControl>

                <Button
                  variant="contained"
                  color="primary"
                  sx={{ height: '40px', minWidth: '80px' }}
                  onClick={handleGoClick}
                >
                  Go
                </Button>
              </Box>


              <Box display="flex" gap={1} sx={{ flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color={isDrawingMode ? "warning" : "secondary"}
                  size="small"
                  onClick={() => {
                    if (isDrawingMode) setDrawingVertices([]);
                    setIsDrawingMode(prev => !prev);
                  }}
                >
                  {isDrawingMode ? "Cancel Drawing" : "Draw Polygon"}
                </Button>
                {isDrawingMode && drawingVertices.length >= 3 && (
                  <Button variant="contained" color="success" size="small" onClick={finishDrawing}>
                    Finish ({drawingVertices.length} pts)
                  </Button>
                )}
              </Box>

              {selectedArea && (
                <Box display="flex" gap={2}>
                  <Button
                    variant="contained"
                    color="info"
                    onClick={savePolygonGIS}
                  >
                    Upload Polygon
                  </Button>

                  <Button
                    variant="contained"
                    color="info"
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              )}

            </Box>
          )}



        </Box>

        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={location || defaultCenter}
            zoom={15}
            onLoad={map => {
              mapRef.current = map;
              window.google.maps.event.addListener(map, 'click', (e: google.maps.MapMouseEvent) => {
                if (!isDrawingModeRef.current || !e.latLng) return;
                setDrawingVertices(prev => [...prev, { lat: e.latLng!.lat(), lng: e.latLng!.lng() }]);
              });
            }}
          >
            {location && (
              <Marker
                position={location}
                icon={{
                  url: '/assets/images/Location-pin_Comway.svg',
                  scaledSize: new window.google.maps.Size(40, 40),
                }}
              />
            )}
            {drawingVertices.length > 1 && (
              <Polygon
                paths={drawingVertices}
                options={{
                  fillColor: "#ff0033",
                  fillOpacity: 0.15,
                  strokeColor: "#ff0033",
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  editable: false,
                  draggable: false,
                  clickable: false,
                }}
              />
            )}
          </GoogleMap>

        ) : (
          <Typography>Loading map...</Typography>
        )}
        <Snackbar
          open={errorSnackbar}
          autoHideDuration={6000}
          onClose={() => setErrorSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 9999 }}
        >
          <Alert
            onClose={() => setErrorSnackbar(false)}
            severity="error"
            sx={{ width: "100%" }}
          >
            Something went wrong. Please try again!
          </Alert>
        </Snackbar>

        <Snackbar
          open={successSnackbar}
          autoHideDuration={3000}
          onClose={() => setSuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 9999 }}
        >
          <Alert
            onClose={() => setSuccessSnackbar(false)}
            severity="success"
            sx={{
              width: '100%',
              bgcolor: '#4caf50',
              color: '#ffffff',
              fontWeight: 'bold',
            }}
          >
            Polygon Created Successfully
          </Alert>
        </Snackbar>
      </Box>

      <DeleteMessage
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirmDelete={handleConfirmDelete}
      />


    </>


  );
};

export default MapComponent;